import { makeCoreLine, makeGradingLine } from './sampleData';
import { resolveAutomationContext } from './sampleChain';
import type { ArrKey, BoolKey, CoreProductLine, GradingGroupLine, MultiSubmitRow, RawSampleTypeGroup, RowOptions, StrKey } from './types';

/** How grading-group lines are applied to a target during a push. */
export type GradingPushMode = 'blank' | 'replace';

/**
 * Label fields: a plain boolean whose checkbox IS the value. Pushed as `true` only
 * when ticked in the source (Case 2). Never pushed in Case 1, and never pushed as `false`
 * — a push only ever ticks a target label, it never clears one.
 */
export const LABEL_FIELDS: BoolKey[] = ['tpDesignDetails', 'tpBom', 'tpTesting', 'tpCare', 'sdTestingReport', 'sdCareLayout'];

/** Keys in RowOptions whose value is a list of `{ value, label }`-shaped options. */
type SelectOptionKey = {
  [K in keyof RowOptions]: RowOptions[K] extends Array<{ value: string }> ? K : never;
}[keyof RowOptions];

/** A check + multi-select field: companion checkbox, the option-array value, and the target options list it validates against. */
export interface OptionField {
  checkedField: BoolKey;
  valueField: ArrKey;
  optionsKey: SelectOptionKey;
}

/** Every check + multi-select field, in column order (Colourway, C&P, Technical Package, Supporting Documents). */
export const OPTION_FIELDS: OptionField[] = [
  { checkedField: 'cwChecked', valueField: 'productColourways', optionsKey: 'colourwayOptions' },
  { checkedField: 'cpChecked', valueField: 'purchaseOrders', optionsKey: 'poOptions' },
  { checkedField: 'tpSpecification', valueField: 'tpSpecificationValue', optionsKey: 'gradingGroups' },
  { checkedField: 'tpSample', valueField: 'tpSampleValue', optionsKey: 'sampleOptions' },
  { checkedField: 'sdDesignDetails', valueField: 'sdDesignDetailsValue', optionsKey: 'designDocs' },
  { checkedField: 'sdColour', valueField: 'sdColourValue', optionsKey: 'colourDocs' },
  { checkedField: 'sdBom', valueField: 'sdBomValue', optionsKey: 'bomDocs' },
  { checkedField: 'sdSpecification', valueField: 'sdSpecificationValue', optionsKey: 'otherDocs' },
  { checkedField: 'sdPomGuide', valueField: 'sdPomGuideValue', optionsKey: 'gradingGroups' },
  { checkedField: 'sdPurchaseOrder', valueField: 'sdPurchaseOrderValue', optionsKey: 'poDocs' },
];

/** Request-Sample text fields — fill empty target in 'blank' mode, overwrite in 'replace' mode. */
export const RS_TEXT_FIELDS: StrKey[] = ['dueDate', 'factory', 'colourway'];

/** Compose-Email text fields — always fill empty target only; never overwritten even in 'replace' mode. */
export const EMAIL_TEXT_FIELDS: StrKey[] = ['emailTo', 'emailCc', 'emailBcc', 'emailMessage'];

/**
 * Costing Information fields filled from source to target on every push, but only when the source
 * carries a value — an empty source field leaves the target untouched (never blanks it). Their option
 * lists (supplier, incoterms, costing stage) are page-level and shared across products, so an
 * id valid on the source is valid on any target. ciCostingTemplate and ciFactory are per-product, so
 * they are validated separately.
 */
export const COSTING_INFO_FIELDS: StrKey[] = [
  'ciReference',
  'ciSupplier',
  'ciCurrency',
  'ciMoqNeeded',
  'ciTargetCost',
  'ciIncoterms',
  'ciShipmentMethod',
];

/** True when a Core Product line has no data in any editable cell (colourway/size/length/quantity/price/total). */
function isCoreLineBlank(line: CoreProductLine): boolean {
  return line.colourway.length === 0 && line.size.length === 0 && line.length.length === 0 && !line.quantity && !line.pricePerUnit && !line.totalPrice;
}

/** True when at least one Core Product line carries data — a push only touches target lines when the source has some. */
export function hasCoreData(row: MultiSubmitRow): boolean {
  return row.coreLines.some((line) => !isCoreLineBlank(line));
}

/**
 * Merge the source's Core Product line stack into the target's, matched line-by-line by position. Each cell
 * is written from the source only when the source carries a value — an empty source cell keeps the target
 * line's existing value, so a push never blanks a target cell. Colourway / size / length are additionally
 * filtered to the values valid in the target product's own core options; when the source offers none valid
 * for a cell, the target line's value is kept. Source lines beyond the target's stack are appended as fresh
 * lines (skipping fully-blank ones); target lines beyond the source's stack are left untouched. Each merged
 * line keeps the target line's own id and per-line `selected` radio — a push syncs cell data, not selection.
 */
function mergeCoreLines(sourceLines: CoreProductLine[], targetLines: CoreProductLine[], targetOptions: RowOptions): CoreProductLine[] {
  const colourwayIds = new Set((targetOptions.coreColourwayOptions ?? []).map((option) => option.value));
  const sizeIds = new Set((targetOptions.coreSizeOptions ?? []).map((option) => option.value));
  const lengthIds = new Set((targetOptions.coreLengthOptions ?? []).map((option) => option.value));

  /** Filtered source values when the source offers at least one valid; otherwise the target's own values. */
  const pickMulti = (sourceValues: string[], targetValues: string[], validIds: Set<string>): string[] => {
    const filtered = sourceValues.filter((value) => validIds.has(value));
    return filtered.length > 0 ? filtered : targetValues;
  };

  const merged = targetLines.map((target, index) => {
    const source = sourceLines[index];
    if (!source) return target;
    return {
      ...target,
      colourway: pickMulti(source.colourway, target.colourway, colourwayIds),
      size: pickMulti(source.size, target.size, sizeIds),
      length: pickMulti(source.length, target.length, lengthIds),
      quantity: source.quantity || target.quantity,
      pricePerUnit: source.pricePerUnit || target.pricePerUnit,
      totalPrice: source.totalPrice || target.totalPrice,
    };
  });

  const appended = sourceLines.slice(targetLines.length).reduce<CoreProductLine[]>((lines, source) => {
    if (isCoreLineBlank(source)) return lines;
    lines.push({
      ...makeCoreLine(),
      colourway: source.colourway.filter((value) => colourwayIds.has(value)),
      size: source.size.filter((value) => sizeIds.has(value)),
      length: source.length.filter((value) => lengthIds.has(value)),
      quantity: source.quantity,
      pricePerUnit: source.pricePerUnit,
      totalPrice: source.totalPrice,
      selected: source.selected,
    });
    return lines;
  }, []);

  return [...merged, ...appended];
}

/** True when the source has no per-field checkbox ticked → Case 1, push every field value. */
export function isWholeRowPush(source: MultiSubmitRow): boolean {
  const anyLabel = LABEL_FIELDS.some((f) => source[f]);
  const anyOption = OPTION_FIELDS.some((o) => source[o.checkedField]);
  return !anyLabel && !anyOption;
}

/** A grading line is blank when every editable field is empty. */
function isLineBlank(line: GradingGroupLine): boolean {
  return !line.gradingGroup && line.size.length === 0 && !line.length && !line.quantity && !line.uom;
}

/** True when at least one grading line carries data (drives whether the grading popup is shown). */
export function hasGradingData(row: MultiSubmitRow): boolean {
  return row.gradingLines.some((l) => !isLineBlank(l));
}

/**
 * Clone grading lines, assigning fresh local ids and dropping any line whose gradingGroup
 * is not in the target's available groups. Size / length / quantity / uom are tied to the
 * gradingGroup — without a valid group we never push those companion values.
 */
function cloneGradingLines(lines: GradingGroupLine[], validGroupIds: Set<string>): GradingGroupLine[] {
  return lines
    .filter((line) => !!line.gradingGroup && validGroupIds.has(line.gradingGroup))
    .map((line) => ({ ...makeGradingLine(), gradingGroup: line.gradingGroup, size: line.size, length: line.length, quantity: line.quantity, uom: line.uom }));
}

/** True when the group is automated — matches the UI cell predicate: the first automation
 *  entry carries a non-empty `configuration[]`. Groups with only a `sampleType._id` but no
 *  configuration are user-picked and must take the non-automation copy branch. */
function hasAutomation(group: RawSampleTypeGroup): boolean {
  return !!group.sampleTypeAutomation?.[0]?.configuration?.length;
}

/**
 * Build the sample-fields slice of a push patch (Sample Type Group, Sample Type, Supplier, chain attachment).
 *
 * Push always writes the source's Sample Type Group to the target (subject to the target's
 * `sampleTypeGroupOptions` eligibility). The `mode` parameter — used elsewhere in buildPushPatch for
 * grading lines and TP/SD multi-selects — does not gate this section: clicking Push is treated as an
 * explicit "sync sample-fields from source" action.
 *
 * Automation source: seeds the target's own first-sample context via resolveAutomationContext, which
 * yields the group's first automation step and a blank chain. Eligibility already guarantees the target
 * has no sample in the group (sampleTypeGroupOptions drops groups the product has already used), so there
 * is never a chain to attach to on this path.
 *
 * Non-automation source: copies the source's manually-picked sampleType + supplier, no chain attachment.
 *
 * rsChecked mirrors the source whenever any sample field is written.
 *
 * Factory / colourway are handled separately as TEXT_FIELDS so they remain field-level fill/overwrite
 * independent of this section.
 */
function buildSampleFieldsPatch(source: MultiSubmitRow, _target: MultiSubmitRow, targetOptions: RowOptions, _mode: GradingPushMode): Partial<MultiSubmitRow> {
  if (!source.sampleTypeGroup) return {};

  const eligibleIds = new Set((targetOptions.sampleTypeGroupOptions ?? []).map((option) => option.value));
  if (!eligibleIds.has(source.sampleTypeGroup)) return {};

  const group = (targetOptions.sampleTypeGroupRaw ?? []).find((rawGroup) => rawGroup._id === source.sampleTypeGroup);
  if (!group) return {};

  if (hasAutomation(group)) {
    const ctx = resolveAutomationContext(group, targetOptions.samplesList ?? []);
    if (!ctx.sampleType) return {};
    return {
      sampleTypeGroup: source.sampleTypeGroup,
      typeOfSampleType: 'main',
      sampleType: ctx.sampleType,
      rsSupplier: ctx.supplier,
      parentId: ctx.parentId,
      revisionRootId: ctx.revisionRootId,
      versionNumber: ctx.versionNumber,
      rsChecked: source.rsChecked,
    };
  }

  if (!source.sampleType) return {};

  return {
    sampleTypeGroup: source.sampleTypeGroup,
    typeOfSampleType: source.typeOfSampleType,
    sampleType: source.sampleType,
    rsSupplier: source.rsSupplier,
    parentId: null,
    revisionRootId: null,
    versionNumber: 1,
    rsChecked: source.rsChecked,
  };
}

/**
 * Build the patch to copy one source row's data onto one target row.
 *
 * Rules:
 * - Labels: pushed as `true` only when ticked in the source (Case 2); never cleared.
 * - Check + multi-select (TP / SD / Colourway / PO): 'blank' mode skips when the target already holds a value;
 *   'replace' mode overwrites. Source values whose id exists in the *target's* options are copied; the checkbox mirrors the source.
 * - Request-Sample text fields (dueDate, factory, colourway): filled when target is empty in 'blank' mode; overwritten in 'replace' mode.
 * - Compose-Email fields (emailTo / Cc / Bcc / Message): always overwrite the target from the source when the
 *   source field has a value (an empty source field leaves the target untouched, so a push never blanks an address).
 * - Grading lines: 'replace' overwrites; 'blank' copies only when the target has no grading data.
 * - Request Sample (group/type/supplier + chain): delegated to buildSampleFieldsPatch; eligibility-checked
 *   against the target's options, latest-sample-resolved for automation groups, manual-supplier for non-automation.
 *
 * Returns only the keys that change, ready for a row merge.
 */
export function buildPushPatch(
  source: MultiSubmitRow,
  target: MultiSubmitRow,
  targetOptions: RowOptions,
  gradingMode: GradingPushMode,
): Partial<MultiSubmitRow> {
  const patch: Record<string, unknown> = {};
  const wholeRow = isWholeRowPush(source);

  if (!wholeRow) {
    LABEL_FIELDS.forEach((f) => {
      if (source[f]) patch[f] = true;
    });
  }

  OPTION_FIELDS.forEach((o) => {
    const consider = wholeRow || source[o.checkedField];
    if (!consider) return;
    if (gradingMode !== 'replace' && target[o.valueField].length > 0) return;
    const validIds = new Set((targetOptions[o.optionsKey] ?? []).map((opt) => opt.value));
    const filtered = source[o.valueField].filter((v) => validIds.has(v.value));
    if (filtered.length === 0) return;
    patch[o.valueField] = filtered;
    patch[o.checkedField] = source[o.checkedField];
  });

  RS_TEXT_FIELDS.forEach((f) => {
    if (!source[f]) return;
    if (gradingMode === 'replace' || !target[f]) patch[f] = source[f];
  });

  EMAIL_TEXT_FIELDS.forEach((f) => {
    if (source[f]) patch[f] = source[f];
  });

  if (hasGradingData(source)) {
    if (gradingMode === 'replace' || !hasGradingData(target)) {
      const validGroupIds = new Set((targetOptions.gradingGroups ?? []).map((option) => option.value));
      const cloned = cloneGradingLines(source.gradingLines, validGroupIds);
      if (cloned.length > 0) patch.gradingLines = cloned;
    }
  }

  Object.assign(patch, buildSampleFieldsPatch(source, target, targetOptions, gradingMode));

  /* Costing Information + Core Product carry no per-field checkbox, so a push syncs them from the source
     under a single rule: an empty source value is never pushed, so it never blanks the target. Every filled
     costing field is copied; core lines are merged cell-by-cell (see mergeCoreLines) only when the source
     has core data, leaving each empty source cell as the target's own value. */
  COSTING_INFO_FIELDS.forEach((field) => {
    if (source[field]) patch[field] = source[field];
  });
  if (source.ciCostingTemplate) {
    const validTemplateIds = new Set((targetOptions.costingTemplateOptions ?? []).map((option) => option.value));
    if (validTemplateIds.has(source.ciCostingTemplate)) patch.ciCostingTemplate = source.ciCostingTemplate;
  }
  if (source.ciFactory) {
    const validFactoryIds = new Set((targetOptions.ciFactoryOptions ?? []).map((option) => option.value));
    if (validFactoryIds.has(source.ciFactory)) patch.ciFactory = source.ciFactory;
  }
  if (hasCoreData(source)) {
    patch.coreLines = mergeCoreLines(source.coreLines, target.coreLines, targetOptions);
  }

  return patch as Partial<MultiSubmitRow>;
}
